import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, Timer } from 'lucide-react'
import { cn } from '@/lib/cn'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import type { AdminStrike } from '@/lib/adapters'
import { fetchActiveStrikes, changeStrikeDuration, BAN_STR as S } from './strikesApi'

function sevClass(n: number): string {
  if (n >= 4) return 'text-danger'
  if (n === 3) return 'text-warning'
  return 'text-success'
}

/**
 * A8.07 — Muddatni o'zgartirish. JONLI aktiv strayklar (`/admin/strikes`,
 * backend `0252c93`) ustidan; muddat-o'zgartirish endpoint
 * (`/admin/strikes/:id/duration`) ishga tushganда ulanadi (POST fail-soft).
 */
export function ChangeDurationScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [target, setTarget] = useState<AdminStrike | null>(null)
  const [days, setDays] = useState('')
  const [busy, setBusy] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const columns = useMemo<AdminColumn<AdminStrike>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 70 },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
      {
        key: 'user_id',
        header: S.userId,
        type: 'text',
        width: 160,
        sortable: false,
        render: (r) =>
          r.user_id ? (
            <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
              {r.username ? `@${r.username}` : `#${r.user_id}`}
            </Link>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        key: 'severity',
        header: S.severity,
        type: 'number',
        width: 100,
        align: 'right',
        render: (r) => <span className={cn('font-mono tabular font-medium', sevClass(r.severity))}>{r.severity}</span>,
      },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      { key: 'expires_at', header: S.duration, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AdminStrike>[]>(
    () => [
      {
        id: 'duration',
        label: S.changeDurationAction,
        icon: <CalendarClock size={14} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (r) => {
          setTarget(r)
          setDays('')
        },
      },
    ],
    [],
  )

  const daysNum = Number(days)
  const daysValid = Number.isInteger(daysNum) && daysNum >= 0

  const apply = async () => {
    if (!target || !daysValid) return
    setBusy(true)
    try {
      await changeStrikeDuration(target.id, daysNum)
      toast(S.durationChanged, { tone: 'success' })
      setTarget(null)
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const fetcher = useCallback(fetchActiveStrikes, [])
  return (
    <>
      <PageHeader title={S.changeDuration} screenId="A8.07" description={S.changeDurationNote} />
      <DataTable<AdminStrike>
        key={reloadKey}
        screenId="A8.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Timer size={28} strokeWidth={1.5} />, title: S.changeDuration }}
      />
      <Dialog
        open={target !== null}
        onClose={() => setTarget(null)}
        title={S.changeDuration}
        icon={<CalendarClock size={16} className="text-warning" />}
        width="sm"
        initialFocusRef={cancelRef}
        footer={
          <>
            <Button variant="warning" disabled={busy || !daysValid} onClick={apply} data-screen="A8.07">
              {S.save}
            </Button>
            <div className="flex-1" />
            <Button ref={cancelRef} variant="default" onClick={() => setTarget(null)}>
              {S.cancel}
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-secondary">{S.newDurationDays}</span>
          <Input
            mono
            inputMode="numeric"
            value={days}
            onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="0"
            autoFocus
          />
        </label>
      </Dialog>
    </>
  )
}
